import { useEffect, useState } from "react";
import Stack from "@mui/material/Stack";
import { updateProductCost } from "../../../services";
import { ProductCost } from "../../../types/product-cost.type";
import { formatNumber } from "../../../utils";
import ConfirmModal from "../../ConfirmModal";
import AmountInput from "../shared/AmountInput";

// Mirrors the API's rule for `product_costs.cost`, a NUMERIC(12,2) column.
const COST_FRACTION_DIGITS = 2;
const MAX_COST = 9999999999.99;

const DIALOG_CONTENT_WIDTH = 360;

/** The typed cost's problem, or null when it can be sent. Negative input cannot be typed. */
const readCostError = (draft: string): string | null => {
  if (draft.trim() === "") return "กรุณากรอกต้นทุน";
  const cost = Number(draft);
  if (!Number.isFinite(cost)) return "กรอกเป็นตัวเลขเท่านั้น";
  if (cost > MAX_COST) return `ต้นทุนต้องไม่เกิน ${formatNumber(MAX_COST)} บาท`;
  return null;
};

interface ProductCostDialogProps {
  /** Row being edited; null keeps the dialog closed. */
  productCost: ProductCost | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function ProductCostDialog({
  productCost,
  onClose,
  onSaved,
}: ProductCostDialogProps) {
  const [draft, setDraft] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);

  const open = productCost !== null;
  const storedCost = productCost?.cost ?? null;
  const initialDraft = storedCost === null ? "" : String(storedCost);

  // Every opening starts from the stored cost, so a cancelled edit never leaks into the next.
  useEffect(() => {
    if (open) setDraft(initialDraft);
  }, [open, initialDraft]);

  const costError = readCostError(draft);
  // An untouched field keeps quiet: a product without a cost opens on an empty box.
  const visibleError = draft === initialDraft ? undefined : costError ?? undefined;
  const isUnchanged = costError === null && Number(draft) === storedCost;

  /** Answers whether the API stored the typed cost. */
  const saveCost = async (productCode: string): Promise<boolean> => {
    try {
      await updateProductCost(productCode, { cost: Number(draft) });
      return true;
    } catch {
      // The axios interceptor already reported the failure; the caller keeps the dialog
      // open on the typed cost so it can be corrected or saved again.
      return false;
    }
  };

  const handleSave = async () => {
    if (!productCost || costError !== null || isUnchanged) return;
    setSaving(true);
    const saved = await saveCost(productCost.product_code);
    setSaving(false);
    if (!saved) return;
    onSaved();
    onClose();
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  return (
    <ConfirmModal
      open={open}
      title="แก้ไขต้นทุนสินค้า"
      description={
        productCost
          ? `${productCost.product_code} ${productCost.product_name ?? "-"}`
          : undefined
      }
      confirmLabel="บันทึก"
      confirmDisabled={costError !== null || isUnchanged}
      loading={saving}
      onClose={handleClose}
      onConfirm={handleSave}
    >
      <Stack sx={{ width: DIALOG_CONTENT_WIDTH, maxWidth: "100%", pt: 2 }}>
        <AmountInput
          label="ต้นทุนต่อหน่วย (บาท)"
          value={draft}
          error={visibleError}
          maxFractionDigits={COST_FRACTION_DIGITS}
          onChange={setDraft}
        />
      </Stack>
    </ConfirmModal>
  );
}
